import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Bell, Check, X, ArrowRight, Trash2, Volume2, VolumeX, Home, Hourglass, TimerOff, CheckCircle2, MessageSquareText, Wallet, PartyPopper, Banknote, CreditCard, XCircle, Star, BellRing, FileText, Copy, Clock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { PaymentReferenceModal } from "@/components/notifications/PaymentReferenceModal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow, differenceInSeconds } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Switch } from "@/components/ui/switch";
import { 
  playPremiumSound, 
  getSoundTypeForNotification, 
  preloadAudioContext,
  isSoundEnabled,
  setSoundEnabled
} from "@/lib/premiumNotificationSounds";
import { triggerNotificationFeedback } from "@/lib/notificationFeedback";

import { tAuto } from "@/lib/tAuto";
export type NotificationBellProps = {
  /** Só o modal centrado, controlado por open/onOpenChange (ex.: menu desktop "Notificações") */
  dialogOnly?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type Notification = {
  id: string;
  user_id?: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  offer_id?: string;
  booking_id?: string;
  data?: any;
};

const NotificationBell = ({ dialogOnly = false, open: openControlled, onOpenChange }: NotificationBellProps) => {
  const { user } = useAuth();
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();
  const isHostOnly = isHost && !isGuest && !isSuperAdmin;
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [soundEnabled, setSoundEnabledState] = useState(true);
  const [hapticEnabled, setHapticEnabled] = useState(true);
  const [internalOpen, setInternalOpen] = useState(false);
  const modalOpen = dialogOnly ? Boolean(openControlled) : internalOpen;
  const setModalOpen = (v: boolean) => {
    if (dialogOnly) onOpenChange?.(v);
    else setInternalOpen(v);
  };
  const [showAll, setShowAll] = useState(false);
  const [isWobbling, setIsWobbling] = useState(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  // Track pending deletions to prevent race condition with realtime
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [refNotif, setRefNotif] = useState<Notification | null>(null);
  const prevUnreadRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);

  // Helpers para descartes locais de notificações virtuais (não-UUID)
  const dismissedKey = user?.id ? `notif:dismissed:${user.id}` : null;
  const loadDismissed = useCallback((): Set<string> => {
    if (!dismissedKey) return new Set();
    try {
      const raw = localStorage.getItem(dismissedKey);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  }, [dismissedKey]);
  const saveDismissed = useCallback((set: Set<string>) => {
    if (!dismissedKey) return;
    try {
      localStorage.setItem(dismissedKey, JSON.stringify(Array.from(set)));
    } catch {}
  }, [dismissedKey]);

  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // 1. Notificações reais da base de dados
      const { data, error } = await (supabase as any)
        .from("push_notifications" as any)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      
      const dbNotifs = (data as Notification[]) || [];
      const virtualNotifs: Notification[] = [];

      // 2. Referências GPR pendentes (Hóspede)
      const { data: pendingPayments } = await supabase
        .from("payments")
        .select(`
          id, transaction_id, amount, currency, reference_entity, reference_number, reference_expires_at, created_at,
          bookings!inner(id, guest_id, properties(title))
        `)
        .eq("status", "pending")
        .eq("method", "GPR")
        .eq("bookings.guest_id", user.id);

      if (pendingPayments && pendingPayments.length > 0) {
        pendingPayments.forEach((p: any) => {
          if (!p.reference_expires_at || new Date(p.reference_expires_at).getTime() < Date.now()) return;
          const notifId = `virtual-gpr-${p.id}`;
          // Evitar duplicados se já existir uma notificação real para a mesma transação
          if (!dbNotifs.some(n => n.data?.transaction_id === p.transaction_id)) {
            const propTitle = p.bookings?.properties?.title || "Estadia";
            virtualNotifs.push({
              id: notifId,
              user_id: user.id,
              title: tAuto("auto.components_notifications_notificationbell.1", "Referência de pagamento pendente"),
              message: `Reserva ${propTitle} · ${Number(p.amount).toLocaleString("pt-AO", { minimumFractionDigits: 2 })} ${p.currency}`,
              type: "payment_reference_pending",
              is_read: false,
              created_at: p.created_at,
              data: {
                booking_id: p.bookings?.id,
                transaction_id: p.transaction_id,
                entity: p.reference_entity,
                reference: p.reference_number,
                amount: p.amount,
                currency: p.currency,
                expires_at: p.reference_expires_at,
                property_title: propTitle
              }
            });
          }
        });
      }

      // 3. Reservas a aguardar aprovação (Proprietário)
      const { data: pendingBookings } = await supabase
        .from("bookings")
        .select(`
          id, check_in, check_out, total_amount, currency, created_at, property_id,
          properties(title)
        `)
        .eq("host_id", user.id)
        .in("status", ["pending", "awaiting_confirmation"]);

      if (pendingBookings && pendingBookings.length > 0) {
        pendingBookings.forEach((b: any) => {
          const notifId = `virtual-host-${b.id}`;
          virtualNotifs.push({
            id: notifId,
            user_id: user.id,
            title: tAuto("auto.components_notifications_notificationbell.2", "Nova Reserva para Aprovar"),
            message: `Tem uma reserva pendente para "${b.properties?.title || 'o seu espaço'}". Aceda às suas reservas para aprovar ou recusar.`,
            type: "host_booking_pending",
            is_read: false,
            created_at: b.created_at,
            data: {
              booking_id: b.id,
              property_id: b.property_id
            }
          });
        });
      }

      // Filtrar virtuais descartados localmente; podar o set para fontes ainda existentes
      const dismissed = loadDismissed();
      const currentVirtualIds = new Set(virtualNotifs.map(v => v.id));
      let dismissedChanged = false;
      for (const id of Array.from(dismissed)) {
        if (id.startsWith("virtual-") && !currentVirtualIds.has(id)) {
          dismissed.delete(id);
          dismissedChanged = true;
        }
      }
      if (dismissedChanged) saveDismissed(dismissed);
      const filteredVirtuals = virtualNotifs.filter(v => !dismissed.has(v.id));

      // Combinar e ordenar por data mais recente
      const allNotifs = [...filteredVirtuals, ...dbNotifs];
      allNotifs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return allNotifs;
    },
    enabled: !!user?.id,
    refetchInterval: 15000,
  });

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`notifications-realtime-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "push_notifications", filter: `user_id=eq.${user.id}` }, (payload) => {
        queryClient.setQueryData(["notifications", user.id], (old: Notification[] = []) => {
          const newNotif = payload.new as Notification;
          if (old.some(n => n.id === newNotif.id)) return old;
          return [newNotif, ...old];
        });
        // Note: triggerAlert is NOT called here - it's handled by the useEffect that monitors notifications
        // This prevents duplicate sounds when a new notification arrives
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "push_notifications", filter: `user_id=eq.${user.id}` }, (payload) => {
        queryClient.setQueryData(["notifications", user.id], (old: Notification[] = []) => {
          return old.map(n => n.id === (payload.new as Notification).id ? payload.new as Notification : n);
        });
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "push_notifications", filter: `user_id=eq.${user.id}` }, (payload) => {
        queryClient.setQueryData(["notifications", user.id], (old: Notification[] = []) => {
          return old.filter(n => n.id !== (payload.old as any).id);
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  // Load sound preferences
  useEffect(() => {
    const loadPrefs = async () => {
      const enabled = await isSoundEnabled();
      setSoundEnabledState(enabled);
    };
    loadPrefs();
  }, []);

  // Toggle sound with persistence
  const toggleSound = async () => {
    const newState = !soundEnabled;
    setSoundEnabledState(newState);
    await setSoundEnabled(newState);
    // Play test sound when enabling
    if (newState) {
      playPremiumSound('default', true);
    }
  };

  const triggerAlert = useCallback(async (notificationType: string = 'default') => {
    setIsWobbling(true);
    
    // Play premium sound
    const soundType = getSoundTypeForNotification(notificationType);
    await playPremiumSound(soundType);
    
    // Also trigger haptic feedback
    triggerNotificationFeedback("info", false, hapticEnabled);
    
    setTimeout(() => setIsWobbling(false), 2000);
  }, [soundEnabled, hapticEnabled]);

  useEffect(() => {
    const currentUnread = notifications.filter((n) => !n.is_read).length;
    
    // Skip sound on initial load - only play for NEW notifications after mount
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      prevUnreadRef.current = currentUnread;
      return;
    }
    
    // Only play sound if unread count INCREASED (new notification arrived)
    if (currentUnread > prevUnreadRef.current) {
      // Get the newest unread notification type
      const newNotifications = notifications.filter((n) => !n.is_read);
      const latestType = newNotifications.length > 0 ? newNotifications[0].type : 'default';
      triggerAlert(latestType);
    }
    prevUnreadRef.current = currentUnread;
  }, [notifications, triggerAlert]);

  // Preload audio context on mount
  useEffect(() => {
    preloadAudioContext();
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    window.dispatchEvent(
      new CustomEvent("berastays:bottom-tabs", {
        detail: { hidden: modalOpen },
      }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("berastays:bottom-tabs", {
          detail: { hidden: false },
        }),
      );
    };
  }, [isMobile, modalOpen]);

  // Agrupa notificações de mensagens da mesma conversa (booking_id) numa única entrada com contador.
  const groupedNotifications = useMemo(() => {
    if (!notifications.length) return notifications;
    const messageGroups = new Map<string, { latest: Notification; count: number }>();
    const others: Notification[] = [];
    for (const n of notifications) {
      if (n.type === "new_message" && n.data?.booking_id) {
        const key = String(n.data.booking_id);
        const existing = messageGroups.get(key);
        if (!existing) {
          messageGroups.set(key, { latest: n, count: n.is_read ? 0 : 1 });
        } else {
          existing.count += n.is_read ? 0 : 1;
          // Mantém a mais recente como base (lista já vem ordenada desc)
        }
      } else {
        others.push(n);
      }
    }
    const merged: Notification[] = [...others];
    for (const { latest, count } of messageGroups.values()) {
      merged.push({
        ...latest,
        title: count > 1
          ? `Nova Mensagem (${count} não lidas)`
          : latest.title,
        data: { ...(latest.data || {}), grouped_count: count, group_booking_id: latest.data?.booking_id },
      });
    }
    merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return merged;
  }, [notifications]);

  const unreadCount = groupedNotifications.filter((n) => !n.is_read).length;
  const displayedNotifications = showAll ? groupedNotifications : groupedNotifications.slice(0, 8);

  const markAsRead = async (notificationId: string) => {
    await (supabase as any).from("push_notifications" as any).update({ is_read: true }).eq("id", notificationId);
    queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
  };

  const markAsUnread = async (notificationId: string) => {
    await (supabase as any).from("push_notifications" as any).update({ is_read: false }).eq("id", notificationId);
    queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;
    await (supabase as any).from("push_notifications" as any).update({ is_read: true }).eq("user_id", user.id).eq("is_read", false);
    queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
  };

  // Returns true while a GPR reference notification is pending payment & within validity (45 min)
  const isPendingReferenceLocked = (notification: Notification): boolean => {
    if (notification.type !== "payment_reference_pending") return false;
    const exp = notification.data?.expires_at ? new Date(notification.data.expires_at) : null;
    if (!exp) return false;
    return exp.getTime() > Date.now();
  };

  const deleteNotification = async (notificationId: string) => {
    // Caminho virtual: descartar localmente (não é UUID, não existe na BD)
    if (notificationId.startsWith("virtual-")) {
      const dismissed = loadDismissed();
      dismissed.add(notificationId);
      saveDismissed(dismissed);
      queryClient.setQueryData(["notifications", user?.id], (old: Notification[] = []) =>
        old.filter(n => n.id !== notificationId)
      );
      return;
    }

    // Mark as pending delete to prevent realtime subscription from restoring it
    setPendingDeletes(prev => new Set(prev).add(notificationId));
    
    // Store previous state for rollback
    const previousNotifications = queryClient.getQueryData(["notifications", user?.id]) as Notification[] || [];
    
    // Optimistic update - remove from UI immediately
    queryClient.setQueryData(["notifications", user?.id], (old: Notification[] = []) => 
      old.filter(n => n.id !== notificationId)
    );
    
    try {
      const { error, count } = await (supabase as any)
        .from("push_notifications" as any)
        .delete()
        .eq("id", notificationId)
        .eq("user_id", user?.id);
      
      if (error) {
        setPendingDeletes(prev => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
        queryClient.setQueryData(["notifications", user?.id], previousNotifications);
        throw error;
      }
      
      if (count === 0) {
        console.warn("Nenhuma notificação foi deletada - verificando permissões RLS");
        setPendingDeletes(prev => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
        queryClient.setQueryData(["notifications", user?.id], previousNotifications);
        toast.error("Notificação não pôde ser eliminada. Verifique permissões.");
        return;
      }
      
      setTimeout(() => {
        setPendingDeletes(prev => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
      }, 1000);
      
    } catch (error: any) {
      console.error("Error deleting notification:", error);
      toast.error("Erro ao eliminar notificação: " + error.message);
    }
  };

  const clearAllNotifications = async () => {
    if (!user?.id) return;
    
    setIsDeleting(true);
    const allIds = notifications.map(n => n.id);
    const virtualIds = allIds.filter(id => id.startsWith("virtual-"));
    const realIds = allIds.filter(id => !id.startsWith("virtual-"));

    // Persistir descartes virtuais localmente
    if (virtualIds.length > 0) {
      const dismissed = loadDismissed();
      virtualIds.forEach(id => dismissed.add(id));
      saveDismissed(dismissed);
    }

    // Mark real ids as pending delete
    setPendingDeletes(prev => {
      const next = new Set(prev);
      realIds.forEach(id => next.add(id));
      return next;
    });
    
    const previousNotifications = queryClient.getQueryData(["notifications", user?.id]) as Notification[] || [];
    
    // Optimistic update - clear UI immediately
    queryClient.setQueryData(["notifications", user?.id], []);
    
    try {
      if (realIds.length > 0) {
        const { error } = await (supabase as any)
          .from("push_notifications" as any)
          .delete()
          .eq("user_id", user.id);
        
        if (error) {
          setPendingDeletes(prev => {
            const next = new Set(prev);
            realIds.forEach(id => next.delete(id));
            return next;
          });
          queryClient.setQueryData(["notifications", user?.id], previousNotifications);
          throw error;
        }
      }
      
      setTimeout(() => {
        setPendingDeletes(new Set());
        setIsDeleting(false);
      }, 1500);
      
    } catch (error: any) {
      console.error("Error clearing notifications:", error);
      toast.error("Erro ao limpar notificações: " + error.message);
      setPendingDeletes(prev => {
        const next = new Set(prev);
        realIds.forEach(id => next.delete(id));
        return next;
      });
      queryClient.setQueryData(["notifications", user?.id], previousNotifications);
      setIsDeleting(false);
    }
  };

  const getNotificationLink = (notification: Notification) => {
    const bookingId = notification.data?.booking_id;
    const offerId = notification.offer_id || notification.data?.offer_id;
    // Para proprietários, todas as secções são renderizadas dentro do Explorar
    if (isHostOnly) {
      switch (notification.type) {
        case "offer_received":
        case "offer_waiting":
        case "offer_expired":
        case "counter_offer_received":
          return `/explorar?menu=pedidos_oferta${offerId ? `&offer=${offerId}` : ""}`;
        case "host_booking_pending":
        case "booking_confirmed":
        case "payment_confirmed":
        case "booking_cancelled":
        case "offer_accepted":
          return `/explorar?menu=reservas${bookingId ? `&booking=${bookingId}` : ""}`;
        case "new_message":
          return "#";
        case "review":
          return `/explorar?menu=avaliacoes`;
        case "commission_earned":
          return `/explorar?menu=financeiro`;
        default:
          if (bookingId) return `/explorar?menu=reservas&booking=${bookingId}`;
          return "/explorar";
      }
    }
    // Hóspedes / afiliados / super admin mantêm as rotas atuais
    switch (notification.type) {
      case "offer_received": return `/ofertas/recebidas${offerId ? `?offer=${offerId}` : ""}`;
      case "offer_waiting": return `/ofertas/nova${offerId ? `?offer=${offerId}` : ""}`;
      case "offer_expired": return `/ofertas/nova${offerId ? `?offer=${offerId}` : ""}`;
      case "offer_accepted_guest": return `/ofertas/nova${offerId ? `?offer=${offerId}` : ""}`;
      case "offer_accepted": return bookingId ? `/reservas/${bookingId}` : "/reservas";
      case "counter_offer_received": return `/ofertas/nova${offerId ? `?offer=${offerId}` : ""}`;
      case "commission_earned": return "/afiliados/painel/transacoes";
      case "booking_confirmed": return bookingId ? `/reservas/${bookingId}` : "/reservas";
      case "payment_confirmed": return bookingId ? `/reservas/${bookingId}` : "/reservas";
      case "payment_reference_pending": return bookingId ? `/reservas/${bookingId}` : "/reservas";
      case "host_booking_pending": return "/dashboard/reservas";
      case "booking_cancelled": return bookingId ? `/reservas/${bookingId}` : "/reservas";
      case "new_message": return "#";
      case "review": return "/reservas";
      default: return "/reservas";
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconClass = "w-5 h-5";
    switch (type) {
      case "offer_received": return <Home className={cn(iconClass, "text-primary")} strokeWidth={1.75} />;
      case "offer_waiting": return <Hourglass className={cn(iconClass, "text-amber-500")} strokeWidth={1.75} />;
      case "offer_expired": return <TimerOff className={cn(iconClass, "text-muted-foreground")} strokeWidth={1.75} />;
      case "offer_accepted":
      case "offer_accepted_guest": return <CheckCircle2 className={cn(iconClass, "text-emerald-500")} strokeWidth={1.75} />;
      case "counter_offer_received": return <MessageSquareText className={cn(iconClass, "text-blue-500")} strokeWidth={1.75} />;
      case "commission_earned": return <Wallet className={cn(iconClass, "text-emerald-600")} strokeWidth={1.75} />;
      case "booking_confirmed": return <PartyPopper className={cn(iconClass, "text-primary")} strokeWidth={1.75} />;
      case "payment_confirmed": return <Banknote className={cn(iconClass, "text-emerald-500")} strokeWidth={1.75} />;
      case "payment_reference_pending": return <FileText className={cn(iconClass, "text-primary")} strokeWidth={1.75} />;
      case "host_booking_pending": return <Home className={cn(iconClass, "text-amber-500")} strokeWidth={1.75} />;
      case "booking_cancelled": return <XCircle className={cn(iconClass, "text-destructive")} strokeWidth={1.75} />;
      case "new_message": return <MessageSquareText className={cn(iconClass, "text-blue-500")} strokeWidth={1.75} />;
      case "review": return <Star className={cn(iconClass, "text-amber-500")} strokeWidth={1.75} />;
      default: return <BellRing className={cn(iconClass, "text-muted-foreground")} strokeWidth={1.75} />;
    }
  };

  const hasActionButton = (type: string) => [
    "offer_accepted", "offer_accepted_guest", "offer_waiting", "offer_expired",
    "counter_offer_received", "booking_confirmed",
    "payment_confirmed", "booking_cancelled", "new_message", "offer_received",
    "host_booking_pending"
  ].includes(type);

  const getActionLabel = (type: string) => {
    switch (type) {
      case "offer_accepted":
      case "offer_accepted_guest":
      case "booking_confirmed":
      case "payment_confirmed": return "Ver reserva";
      case "host_booking_pending": return "Analisar reserva";
      case "booking_cancelled": return "Ver detalhes";
      case "counter_offer_received": return "Ver proposta";
      case "offer_received": return "Ver oferta";
      case "offer_waiting": return "Ver ofertas";
      case "offer_expired": return "Tentar novamente";
      case "new_message": return "Ver mensagem";
      default: return "Ver";
    }
  };

  if (!user) return null;

  // Live countdown component for "offer_waiting" notifications
  const OfferCountdown = ({ expiresAt }: { expiresAt?: string }) => {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
      if (!expiresAt) return;
      const id = setInterval(() => setNow(new Date()), 1000);
      return () => clearInterval(id);
    }, [expiresAt]);
    if (!expiresAt) return null;
    const secs = Math.max(0, differenceInSeconds(new Date(expiresAt), now));
    if (secs === 0) return <span className="text-[10px] font-semibold text-destructive">Expirada</span>;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-md">
        ⏱ {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")} restantes
      </span>
    );
  };

  // Live countdown for GPR pending references (up to 45 min)
  const ReferenceCountdown = ({ expiresAt }: { expiresAt: string }) => {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
      const id = setInterval(() => setNow(new Date()), 1000);
      return () => clearInterval(id);
    }, []);
    const secs = Math.max(0, differenceInSeconds(new Date(expiresAt), now));
    if (secs === 0) {
      return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">Expirada</span>;
    }
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const label = h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-md">
        ⏱ {label} · pendente
      </span>
    );
  };



  const bellButton = (
    <motion.button
      className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-white to-gray-50/90 dark:bg-none dark:bg-[#1C1C1E] backdrop-blur-xl border border-[#E8513D]/30 dark:border-transparent shadow-md dark:shadow-none hover:bg-muted/80 transition-all duration-200 active:scale-95"
      whileTap={{ scale: 0.95 }}
      onClick={() => setModalOpen(true)}
      animate={isWobbling ? { rotate: [0, -12, 10, -10, 8, -6, 4, -2, 0], scale: [1, 1.15, 1.1, 1.12, 1.08, 1.04, 1.02, 1] } : {}}
      transition={isWobbling ? { duration: 0.8, ease: "easeInOut" } : {}}
    >
      <Bell className="w-5 h-5 text-[#E8513D]" strokeWidth={1.8} />
      <AnimatePresence>
        {unreadCount > 0 && (
          <motion.span initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }} className="absolute -top-0.5 -right-0.5 min-w-[20px] h-[20px] px-1 flex items-center justify-center bg-destructive text-white text-[11px] font-bold rounded-full shadow-lg">
            {unreadCount > 99 ? "99+" : unreadCount}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );

  const notifContent = (
    <div className="overflow-hidden rounded-xl">
      {/* Header */}
      <div 
        className="relative flex items-center justify-between px-4 py-3.5 border-b border-border/30 bg-card/80 pr-12 cursor-grab active:cursor-grabbing select-none"
        style={{ touchAction: "none" }}
      >
        {/* Drag handle indicator */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-muted-foreground/20" />
        <div className="flex items-center gap-2.5 mt-1">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-sm">
            <Bell className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-foreground">{tAuto("auto.components_notifications_notificationbell.3", "Notificações")}</h3>
            {unreadCount > 0 && <p className="text-[11px] text-muted-foreground">{unreadCount} não lida{unreadCount > 1 ? 's' : ''}</p>}
          </div>
        </div>
        <div className={cn("flex items-center gap-2", isMobile && "gap-4")} onPointerDown={(e) => e.stopPropagation()}>
          <div className={cn("flex items-center gap-2", isMobile && "gap-4")}>
            <button
              type="button"
              className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title={soundEnabled ? "Som: ligado" : "Som: desligado"}
              onClick={toggleSound}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <Switch
              checked={soundEnabled}
              onCheckedChange={toggleSound}
              aria-label={tAuto("auto.components_notifications_notificationbell.4", "Ativar som de notificações")}
            />
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="Marcar todas como lidas">
              <Check className="w-4 h-4" />
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={() => setShowClearAllDialog(true)} className="p-2 rounded-xl hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive" title="Limpar todas">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <ScrollArea 
        className={cn("transition-all duration-300 overflow-y-auto", isMobile ? (showAll ? "h-[75vh] max-h-[75vh]" : "h-[65vh] max-h-[65vh]") : (showAll ? "h-[60vh] max-h-[60vh]" : "h-[400px] max-h-[400px]"))}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {notifications.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-muted/50 flex items-center justify-center">
              <Bell className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">{tAuto("auto.components_notifications_notificationbell.5", "Sem notificações")}</p>
            <p className="text-xs text-muted-foreground/70 mt-1">{tAuto("auto.components_notifications_notificationbell.6", "As suas notificações aparecerão aqui")}</p>
          </div>
        ) : (
          <div className="divide-y divide-border/30">
            <AnimatePresence initial={false}>
              {displayedNotifications.map((notification, index) => (
                <motion.div key={notification.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}>
                  <div className={cn("p-3.5 hover:bg-muted/40 transition-all duration-200", !notification.is_read && "bg-primary/4 border-l-2 border-l-primary")}>
                    <Link to={getNotificationLink(notification)} onClick={(e) => { 
                      if (notification.type === "new_message") {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent("open-bera-ia-widget"));
                      }
                      if (!notification.is_read) markAsRead(notification.id); 
                      setModalOpen(false); 
                    }} className="flex gap-3">
                      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", !notification.is_read ? "bg-primary/10" : "bg-muted/50")}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn("text-sm font-semibold leading-tight", !notification.is_read ? "text-foreground" : "text-foreground/80")}>{notification.title}</p>
                          {!notification.is_read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                        </div>
                        <p className="text-xs text-foreground/70 line-clamp-2 mt-0.5">{notification.message}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <p className="text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: getDateLocale() })}</p>
                          {notification.type === "offer_waiting" && (
                            <OfferCountdown expiresAt={notification.data?.expires_at} />
                          )}
                          {notification.type === "payment_reference_pending" && notification.data?.expires_at && (
                            <ReferenceCountdown expiresAt={notification.data.expires_at} />
                          )}
                        </div>

                      </div>
                    </Link>
                    <div className="mt-2 ml-[52px] flex items-center gap-1.5 flex-wrap">
                      {notification.type === "payment_reference_pending" && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px] rounded-lg border-primary/30 text-primary hover:bg-primary/10 gap-1"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRefNotif(notification);
                            if (!notification.is_read) markAsRead(notification.id);
                          }}
                        >
                          <FileText className="w-3 h-3" />
                          Ver dados de pagamento
                        </Button>
                      )}
                      {hasActionButton(notification.type) && !notification.is_read && (
                        <Button asChild size="sm" variant="outline" className="h-7 text-[11px] rounded-lg border-primary/30 text-primary hover:bg-primary/10 gap-1" onClick={(e) => { 
                          if (notification.type === "new_message") {
                            e.preventDefault();
                            window.dispatchEvent(new CustomEvent("open-bera-ia-widget"));
                          }
                          markAsRead(notification.id); 
                          setModalOpen(false); 
                        }}>
                          <Link to={getNotificationLink(notification)}>{getActionLabel(notification.type)}</Link>
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[11px] rounded-lg"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (notification.is_read) markAsUnread(notification.id);
                          else markAsRead(notification.id);
                        }}
                        title={notification.is_read ? "Marcar como não lida" : "Marcar como lida"}
                      >
                        {notification.is_read ? "Não lida" : "Lida"}
                      </Button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPendingReferenceLocked(notification)) {
                            toast.info("Esta referência está pendente. Só pode ser eliminada após o pagamento ou expiração.");
                            return;
                          }
                          deleteNotification(notification.id);
                        }}
                        disabled={isPendingReferenceLocked(notification)}
                        className={cn(
                          "p-1 rounded-md transition-colors",
                          isPendingReferenceLocked(notification)
                            ? "text-muted-foreground/30 cursor-not-allowed"
                            : "hover:bg-destructive/10 text-muted-foreground/50 hover:text-destructive"
                        )}
                        title={isPendingReferenceLocked(notification) ? "Bloqueada enquanto o pagamento está pendente" : "Eliminar"}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>

                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </ScrollArea>

      {notifications.length > 8 && (
        <div className="p-2 border-t border-border/30 bg-muted/20" onPointerDown={(e) => e.stopPropagation()}>
          <button onClick={() => setShowAll(!showAll)} className="w-full py-2 text-center text-xs font-medium text-primary hover:text-primary/80 transition-colors rounded-lg hover:bg-muted/50">
            {showAll ? "Ver menos" : `Ver mais ${notifications.length - 8} notificações`}
          </button>
        </div>
      )}

      {/* Clear All Confirmation Dialog */}
      <AlertDialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tAuto("auto.components_notifications_notificationbell.7", "Limpar todas as notificações?")}</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja eliminar todas as {notifications.length} notificações? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tAuto("auto.components_notifications_notificationbell.8", "Cancelar")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => { clearAllNotifications(); setShowClearAllDialog(false); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Sim, limpar tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  const dialogContentClass = cn(
    "p-0 border-border/40 shadow-2xl rounded-2xl overflow-hidden z-[10050]",
    dialogOnly || !isMobile
      ? "w-[440px] max-w-[90vw]"
      : "w-[calc(100vw-16px)] max-w-[460px]"
  );

  // Persisted drag position (per user, per device)
  const posKey = user?.id ? `notif:pos:${user.id}` : null;
  const loadPos = () => {
    if (!posKey) return { x: 0, y: 0 };
    try {
      const raw = localStorage.getItem(posKey);
      if (!raw) return { x: 0, y: 0 };
      const p = JSON.parse(raw);
      return { x: Number(p.x) || 0, y: Number(p.y) || 0 };
    } catch { return { x: 0, y: 0 }; }
  };
  const initialPos = useRef(loadPos()).current;
  const xMV = useMotionValue(initialPos.x);
  const yMV = useMotionValue(initialPos.y);

  // Reset transform to last saved when reopening
  useEffect(() => {
    if (modalOpen) {
      const p = loadPos();
      xMV.set(p.x);
      yMV.set(p.y);
    }
  }, [modalOpen]);

  return (
    <>
      {!dialogOnly && bellButton}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent 
          className={dialogContentClass} 
          overlayClassName="bg-black/20 backdrop-blur-none"
          draggable
          dragListener={true}
          style={{ x: xMV, y: yMV } as any}
          onDragEnd={() => {
            if (!posKey) return;
            try {
              localStorage.setItem(posKey, JSON.stringify({ x: xMV.get(), y: yMV.get() }));
            } catch {}
          }}
        >
          {notifContent}
        </DialogContent>
      </Dialog>
      <PaymentReferenceModal
        notification={refNotif}
        onClose={() => setRefNotif(null)}
      />
    </>
  );
};
export default NotificationBell;
